import { Alert, Box, Heading, Stepper, TextLink } from '@mm/components';
import * as allPermissions from '@mm/constants/facebook-permissions';
import { useResolutionInfo } from '@mm/hooks';
import { InfoIcon } from '@mm/icons';
import { ErrorReportingUtils } from '@mm/plugins';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { AnonymousBotController } from '@mm/state/controllers/AnonymousBotController';
import { ResponseOrError } from '@mm/state/errorHandlers/common-handler';
import { AvailablePageWithUniqueName } from '@mm/state/interfaces/connected-page';
import CompanyModel from '@mm/state/model/CompanyModel';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import onboardingActions from '@mm/state/modules/onboardingState/actions';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { selectCompanies } from '@mm/state/selectors/viewSelectors';
import { selectWidgetsByDialogueId } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson } from '@mm/utils';
import HeroImage from 'assets/hero-image.png';
import Hero2Image from 'assets/hero2.png';
import PhonesImage from 'assets/phones.png';
import classNames from 'classnames';
import { difference, head } from 'lodash';
import * as queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import FacebookLoginButton from '../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import { PrimaryButton } from '../../../common/buttons/PrimaryButton/PrimaryButton';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import {
  EmailInput,
  Payload,
} from '../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { IceBreakerPreview } from '../../../editor/features/facebookPageWelcomer/IceBreaker/IceBreakerPreview/IceBreakerPreview';
import { SimpleIceBreaker } from '../../../editor/features/facebookPageWelcomer/IceBreaker/SimpleIceBreaker';
import { StartBotsDialogueEditor } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { Routes } from '../../../routes/top-level-routes';
import { trackSignUpEventGA } from '../../MarketingAnalytics';
import facebookBadgeOne from './badge-1.png';
import facebookBadgeTwo from './badge-2.png';
import './FreeLeadGeneratorBuilder.pcss';
import larryImg from './MarketingPromoCard/larry-promo-bg.jpg';
import { MarketingPromoCard } from './MarketingPromoCard/MarketingPromoCard';
import nailImg from './MarketingPromoCard/nail-promo-bg.jpg';
import { useStandaloneBot } from './use-standalone-bot';

type Props = {
  className?: string;
};

type Step = {
  label: string;
  name: string;
  title: string;
  subtitle?: string;
};

const freeLeadGeneratorSteps: Step[] = [
  {
    label: 'Step 1',
    name: 'create',
    title: "Let's create your Free Facebook Lead Generator!",
  },
  {
    label: 'Step 2',
    name: 'connect_page',
    title: 'Connect your Facebook Page',
  },
  { label: 'Step 3', name: 'email', title: 'Where should we send your leads?' },
];

const automatedResponseSteps: Step[] = [
  {
    label: 'Step 1',
    name: 'create',
    title: 'Let’s create some automated responses!',
    subtitle:
      'Design the Dialogue people will receive when they message your Facebook Page with the Send Message button or comment on your Facebook Posts',
  },
  {
    label: 'Step 2',
    name: 'add_ice_breakers',
    title: "Let's add some icebreakers",
    subtitle:
      'Add some Frequently Asked Questions to help customers start a conversation on your Facebook Page.',
  },
  {
    label: 'Step 3',
    name: 'connect_page',
    title: 'Connect your Facebook Page',
  },
];

const useBuilderData = (
  source: string,
  path: string,
  eventType: string,
  steps: { label: string; name: string; title: string; subtitle?: string }[],
  openEmailModal: boolean = false,
) => {
  const { localToken, bot } = useStandaloneBot(source);

  const session = useSelector(selectSessionSelector);
  const widgets = useSelector(selectWidgetsByDialogueId(bot?.page.id));

  const companyCRUDActions = useCompanyCRUDActions();
  const usersCRUDActions = useUsersCRUDActions();

  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();
  const dispatch: Dispatch<StoreState> = useDispatch();

  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const isLastStep = activeStepIdx === steps.length - 1;

  const [emailState, setEmailState] = useState<{
    email: string;
    isValid: boolean;
  }>({
    email: '',
    isValid: false,
  });
  const onEmailChange = ({ email, isValid }: Payload) => {
    setEmailState({
      email,
      isValid,
    });
  };
  const [connectedPage, setConnectedPage] = useState<
    ConnectedPageModel | undefined
  >();

  const router = useRouter();
  const companies = useSelector(selectCompanies);

  const [permissionError, setPermissionError] = useState(false);
  const [initialToken, setInitialToken] = useState<string | undefined>();
  const [initialGrantedScopes, setInitialGrantedScopes] = useState<string[]>(
    [],
  );

  const onTryAgainBtnClick = () => {
    trackWebAppGAEvent('click', {
      category: eventType,
      label: `try_again_btn`,
    });
    setPermissionError(false);
  };

  const onTestBtnClick = () => {
    trackWebAppGAEvent('click', {
      category: eventType,
      label: `test_bot_btn`,
    });
    if (connectedPage && bot && session) {
      const url = `http://m.me/${connectedPage.remoteId}?ref=${bot.page.searchToken}_${session.searchToken}`;
      window.open(url);
    }
  };

  const onStepsComplete = (
    email?: string,
    page?: AvailablePageWithUniqueName,
    botId?: number, // target bot might be different from state
  ) => {
    if (bot) {
      const businessEmail =
        email ||
        companies.find(({ id }) => page?.companyId === id)?.businessEmail ||
        session?.email;

      localToken.remove();
      return router.history.push(`${path}/congratulations`, {
        botId: botId || bot.id,
        businessEmail,
      });
    }
  };

  const onProceedBtnClick = async () => {
    const { email } = emailState;

    const companyIdToUse = connectedPage?.companyId || bot?.companyId;
    // page is source of truth as bot companyId is mock

    if (isLastStep && email && bot && session && companyIdToUse) {
      const companyUpdateOk = await companyCRUDActions.update({
        id: companyIdToUse,
        businessEmail: email,
      });

      if (companyUpdateOk) {
        // BE creates new user on fb login so we are using session.id here as bot.userId is obsolete now
        await usersCRUDActions.updateUserAttributes(
          companyIdToUse,
          session.id,
          {
            onboardingSignupStep: 'none' as OnboardingSignupStep,
          },
        );
        dispatch(onboardingActions.setJustCompletedOnboarding(true)); // affects modal open ordering
        trackWebAppGAEvent('click', {
          category: eventType,
          label: `continue_btn:congratulations`,
        });
        return onStepsComplete(email);
      }
    }

    const nextStep = activeStepIdx + 1;
    trackWebAppGAEvent('click', {
      category: eventType,
      label: `continue_btn:${steps[nextStep]?.name}`,
    });
    setActiveStepIdx(nextStep);
  };

  const onLoginSuccess = async ({
    facebookToken,
    grantedPermissions,
    requestedPermissions,
  }: {
    facebookToken: string;
    grantedPermissions: string[];
    requestedPermissions: string[];
  }) => {
    // check if all requested permissions are granted
    const diff = difference(requestedPermissions, grantedPermissions);
    if (diff.length) {
      trackWebAppGAEvent('login', {
        category: eventType,
        label: `login_failure_permissions`,
      });
      ErrorReportingUtils.reportError(
        'Standalone Bot Error: permission error',
        { extraData: { grantedPermissions, requestedPermissions } },
      );
      setPermissionError(true);
      return;
    }

    trackWebAppGAEvent('login', {
      category: eventType,
      label: `login_success`,
    });

    if (bot) {
      const res = await facebookSessionCRUDActions.create({
        src: `${source}_converted`,
        facebookToken,
        standaloneToken: bot.standaloneToken,
      });
      if (res) {
        const openConnectModal = () =>
          dispatch(
            modalActions.openConnectPagesModal({
              canSelectMultiplePages: false,
              company: { id: bot.companyId } as CompanyModel,
              connectedPageButton: {
                text: 'Apply',
                onClick: (
                  page: AvailablePageWithUniqueName,
                  closeModal: () => void,
                ) => {
                  trackWebAppGAEvent('click', {
                    category: eventType,
                    label: `connect_page_apply_btn:apply`,
                  });
                  const { remoteId: remotePageId } = page;
                  if (remotePageId && bot.id) {
                    dispatch(
                      modalActions.openGeneralConfirmModal({
                        onCloseHandler: async ok => {
                          if (ok) {
                            trackWebAppGAEvent('click', {
                              category: eventType,
                              label: `connect_page_apply_btn:confirm`,
                            });
                            try {
                              const { success, bot: connectedBot } =
                                await AnonymousBotController.connect({
                                  remotePageId,
                                  standaloneToken: bot.standaloneToken,
                                });
                              if (success) {
                                trackWebAppGAEvent('page_apply', {
                                  category: eventType,
                                  label: `page_apply_success`,
                                });
                                closeModal();
                                return onStepsComplete(
                                  undefined,
                                  page,
                                  connectedBot.id,
                                );
                              }
                            } catch (err) {
                              const body: {
                                errors?: string[];
                                status?: number;
                              } = (await getErrorJson(err)) || {};

                              trackWebAppGAEvent('page_apply', {
                                category: eventType,
                                label: `page_apply_failure`,
                              });

                              ErrorReportingUtils.reportError(
                                'Standalone Bot Error: page apply error',
                                { extraData: { err: JSON.stringify(body) } },
                              );

                              if (err.status === 422) {
                                const errorText = head(body?.errors);
                                if (errorText) {
                                  return dispatch(
                                    modalActions.openGeneralTextModal({
                                      header: 'Permissions',
                                      message: errorText,
                                      closeButtonText: undefined,
                                      onClose: openConnectModal,
                                    }),
                                  );
                                }
                              }
                            }
                          }
                          trackWebAppGAEvent('click', {
                            category: eventType,
                            label: `connect_page_apply_btn:denny`,
                          });
                          openConnectModal();
                        },
                        headerText: 'Heads up!',
                        messageText: `This will overwrite the current Chatbot on ${page.name}`,
                      }),
                    );
                  }
                },
              },
              standaloneToken: bot.standaloneToken,
              onError: async (error: ResponseOrError) => {
                if (error instanceof Response) {
                  trackWebAppGAEvent('page_connect', {
                    category: eventType,
                    label: `page_connect_failure`,
                  });
                  const err: { errors?: string[] } = await getErrorJson(error);
                  ErrorReportingUtils.reportError(
                    'Standalone Bot Error: page connect error',
                    {
                      extraData: {
                        error: JSON.stringify(error),
                        err: JSON.stringify(err),
                      },
                    },
                  );
                  if (error.status === 403) {
                    setPermissionError(true);
                  }

                  if (error.status === 422) {
                    const errorText = err?.errors?.[0];
                    if (errorText) {
                      return dispatch(
                        modalActions.openGeneralTextModal({
                          header: 'Permissions',
                          message: errorText,
                          closeButtonText: undefined,
                          onClose: openConnectModal,
                        }),
                      );
                    }
                  }
                }
              },
              onSuccess: page => {
                trackWebAppGAEvent('page_connect', {
                  category: eventType,
                  label: `page_connect_success`,
                });
                trackSignUpEventGA();
                setConnectedPage(page);
                if (openEmailModal) {
                  dispatch(
                    modalActions.openEmailSubmissionModal({
                      onEmailChange,
                      onStepsComplete: async (email: string) => {
                        const companyIdToUse =
                          page?.companyId || bot?.companyId;
                        if (companyIdToUse && email) {
                          const companyUpdateOk =
                            await companyCRUDActions.update({
                              id: companyIdToUse,
                              businessEmail: email,
                            });

                          if (companyUpdateOk) {
                            await usersCRUDActions.updateUserAttributes(
                              companyIdToUse,
                              res.entity.id,
                              {
                                onboardingSignupStep:
                                  'none' as OnboardingSignupStep,
                              },
                            );
                            dispatch(
                              onboardingActions.setJustCompletedOnboarding(
                                true,
                              ),
                            ); // affects modal open ordering
                            trackWebAppGAEvent('click', {
                              category: eventType,
                              label: `continue_btn:congratulations`,
                            });
                          }
                        }
                        onStepsComplete(email);
                      },
                      emailState,
                      onClose: () =>
                        router.history.push(`${path}/congratulations`, {
                          botId: bot.id,
                          businessEmail:
                            companies.find(({ id }) => page?.companyId === id)
                              ?.businessEmail || session?.email,
                        }),
                    }),
                  );
                }
              },
            }),
          );
        openConnectModal();
      }
    }
  };

  useEffect(() => {
    let token;
    let error;
    if (router.location.search) {
      const params = queryString.parse(router.location.search);

      if (params.error) {
        error = true;
        setPermissionError(true);
      }
    }
    if (router.location.hash) {
      const params = queryString.parse(router.location.hash);
      const facebookToken = String(params.access_token);
      const scopes = String(params.granted_scopes);
      if (facebookToken) {
        token = facebookToken;
        setInitialToken(token);
      }
      if (scopes) {
        setInitialGrantedScopes(scopes.split(','));
      }
    }
    if (token || error) {
      setActiveStepIdx(
        steps.findIndex(step => step.name === 'connect_page') || 0,
      );
    }
  }, []);

  useEffect(() => {
    if (bot && initialToken && initialGrantedScopes.length > 0) {
      onLoginSuccess({
        facebookToken: initialToken,
        grantedPermissions: initialGrantedScopes,
        requestedPermissions: allPermissions.getArrayOfPermissions(
          allPermissions.ALL_PERMISSIONS,
        ),
      });
    }
  }, [bot, initialToken, initialGrantedScopes]);

  return {
    bot,
    widgets,
    activeStepIdx,
    isLastStep,
    onEmailChange,
    emailState,
    connectedPage,
    onProceedBtnClick,
    permissionError,
    onTryAgainBtnClick,
    onTestBtnClick,
    onLoginSuccess,
  };
};

export const AutomatedResponseBuilder = ({ className }: Props) => {
  const steps = automatedResponseSteps;
  const eventType = 'automated_response_tool';
  const {
    bot,
    widgets,
    activeStepIdx,
    isLastStep,
    connectedPage,
    onProceedBtnClick,
    permissionError,
    onTryAgainBtnClick,
    onTestBtnClick,
    onLoginSuccess,
  } = useBuilderData(
    'automated_response_tool',
    Routes.automatedResponseTool.path,
    eventType,
    steps,
    true,
  );

  const iceBreaker = useSelector(
    (state: StoreState) => state.iceBreakers.facebook.entities[0],
  );
  const [iceBreakerValid, setIceBreakerValid] = useState(true);

  const dynamicTitle = steps[activeStepIdx]?.title;
  const dynamicSubtitle = steps[activeStepIdx]?.subtitle;
  const continueBtnDisabled = activeStepIdx === 1 && !iceBreakerValid;

  const { isDesktop } = useResolutionInfo();

  return (
    <div
      className={classNames(
        className,
        'automated-response-builder',
        'free-lead-generator-builder',
        {
          'free-lead-generator-builder--desktop': isDesktop,
        },
      )}
    >
      <div className="free-lead-generator-builder__container">
        <Box
          className={`free-lead-generator-builder__header free-lead-generator-builder__header-step-${
            activeStepIdx + 1
          }`}
        >
          <MarketingPromoCard
            className="free-lead-generator-builder__marketing-card"
            step={activeStepIdx + 1}
            contentMapOverride={{
              3: {
                src: nailImg,
                body:
                  'For some reason, not very many marketers are taking advantage of\n' +
                  'Messanger marketing, even though it’s the hottest opportunity in\n' +
                  'digital marketing today.',
                name: 'Neil Patel',
                position: 'Forbes Top 10 Marketer',
              },
              1: {
                src: larryImg,
                body:
                  'Marketers can use Customers.ai to connect with customers on the most\n' +
                  'popular mobile chat application in the U.S., Facebook Messenger, used by over a billion people every day.',
                name: 'Larry Kim',
                position: 'CEO & Co-founder, Customers.ai',
              },
            }}
          />
          <div className="free-lead-generator-builder__header-content">
            <div className="free-lead-generator-builder__header-content-wrapper">
              <div className="free-lead-generator-builder__images">
                <div className="free-lead-generator-builder__badge free-lead-generator-builder__badge-left">
                  <img
                    src={facebookBadgeOne}
                    alt="Facebook Approval Badge"
                    width="94px"
                  />
                </div>

                <div className="free-lead-generator-builder__stepper-with-logo">
                  <Logo className="free-lead-generator-builder__logo" />
                  <Stepper
                    className="free-lead-generator-builder__stepper"
                    markFormerComplete
                    activeIdx={activeStepIdx}
                    steps={steps}
                  />
                </div>

                <div className="free-lead-generator-builder__badge free-lead-generator-builder__badge-right">
                  <img
                    src={facebookBadgeTwo}
                    alt="Facebook Approval Badge"
                    width="120px"
                  />
                </div>
              </div>

              {dynamicTitle && (
                <>
                  <span className="free-lead-generator-builder__header-title">
                    <Heading
                      size={3}
                      text={`Step ${activeStepIdx + 1}: `}
                      className="free-lead-generator-builder__attention"
                    />
                    <Heading size={3} text={dynamicTitle} />
                  </span>
                  {dynamicSubtitle && (
                    <TextLink className="free-lead-generator-builder__helper-text">
                      {dynamicSubtitle}
                    </TextLink>
                  )}
                </>
              )}

              {activeStepIdx === 0 && (
                <div className="free-lead-generator-builder__images-container">
                  <div className="free-lead-generator-builder__hero-img-wrapper">
                    <img
                      src={HeroImage}
                      className="free-lead-generator-builder__hero-img"
                      alt="Facebook Hero"
                    />
                  </div>
                  <div className="free-lead-generator-builder__hero2-img-wrapper">
                    <img
                      src={Hero2Image}
                      className="free-lead-generator-builder__hero2-img"
                      alt="Facebook Hero"
                    />
                  </div>
                </div>
              )}

              {activeStepIdx === 2 && !permissionError && !connectedPage && (
                <>
                  <TextLink className="free-lead-generator-builder__helper-text">
                    Great job! Now just sign in with Facebook below and choose
                    your page from the list.
                  </TextLink>
                  <TextLink className="free-lead-generator-builder__helper-text">
                    We&apos;ll connect your new Chatbot and you can test it out!
                  </TextLink>{' '}
                  <FacebookLoginButton
                    className="free-lead-generator-builder__action-btn"
                    allowManualFlow={true}
                    redirectPath={`${Routes.automatedResponseTool.path}/start`}
                    onSuccess={onLoginSuccess}
                  />
                </>
              )}

              {activeStepIdx === 2 && permissionError && (
                <>
                  <Alert
                    type="error"
                    className="free-lead-generator-builder__info-message"
                  >
                    Oops! Looks like we don&apos;t have the permission we need
                    to connect to your page. Let&apos;s try that again.
                  </Alert>
                  <PrimaryButton
                    size="lg"
                    onClick={onTryAgainBtnClick}
                    className="free-lead-generator-builder__action-btn"
                  >
                    Try again
                  </PrimaryButton>
                </>
              )}

              {activeStepIdx === 2 && connectedPage && (
                <>
                  <Alert
                    type="success"
                    className="free-lead-generator-builder__info-message"
                    showInfoIcon
                  >
                    Success! We&apos;re connected. Now just click the button
                    below to test your Free Facebook Lead Generator.
                  </Alert>
                  <PrimaryButton
                    size="lg"
                    onClick={onTestBtnClick}
                    className="free-lead-generator-builder__action-btn"
                  >
                    Test
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
          {activeStepIdx === 1 && (
            <div className="free-lead-generator-builder__phones-container">
              <img
                src={PhonesImage}
                className="free-lead-generator-builder__phones-img"
                alt="Icebreakers Iphone"
              />
            </div>
          )}
        </Box>

        {activeStepIdx === 0 && (
          <>
            <Heading
              className="free-lead-generator-builder__title"
              size={3}
              text="Create a Dialogue for people who message your Facebook Page with the Send Message button"
            />
            <div className="free-lead-generator-builder__subtitle">
              <InfoIcon className="free-lead-generator-builder__info-icon" />
              <span>
                Click the grey boxes to customize the greetings and questions.
                Add more questions or images by clicking the "+".
              </span>
            </div>
            {bot && (
              <StartBotsDialogueEditor
                className="free-lead-generator-builder__editor"
                chatbotId={bot?.id}
                dialogueId={bot?.page?.id}
                widgets={widgets as WidgetModel[]}
                platforms={['facebook']}
              />
            )}
          </>
        )}
        {activeStepIdx === 1 && bot && (
          <div className="free-lead-generator-builder__icebreakers">
            <div className="free-lead-generator-builder__icebreakers-container">
              <div className="free-lead-generator-builder__icebreakers-wrapper">
                <SimpleIceBreaker
                  token={bot.standaloneToken}
                  botId={bot.id}
                  onValidate={valid => setIceBreakerValid(valid)}
                />
              </div>
            </div>
            {iceBreaker && <IceBreakerPreview iceBreaker={iceBreaker} />}
          </div>
        )}
        <div className="free-lead-generator-builder__footer">
          {!isLastStep && (
            <PrimaryButton
              disabled={continueBtnDisabled}
              size="lg"
              onClick={onProceedBtnClick}
            >
              Continue
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export const FreeLeadGeneratorBuilder = ({ className }: Props) => {
  const steps = freeLeadGeneratorSteps;
  const eventType = 'free_lead_generator';
  const {
    bot,
    widgets,
    activeStepIdx,
    isLastStep,
    onEmailChange,
    emailState,
    connectedPage,
    onProceedBtnClick,
    permissionError,
    onTryAgainBtnClick,
    onTestBtnClick,
    onLoginSuccess,
  } = useBuilderData(
    'free_lead_generator',
    Routes.freeLeadGenerator.path,
    eventType,
    steps,
  );

  const dynamicTitle = steps[activeStepIdx]?.title;
  const continueBtnDisabled =
    (activeStepIdx === 1 && !connectedPage) ||
    (isLastStep && !emailState.isValid);

  const { isDesktop } = useResolutionInfo();

  return (
    <div
      className={classNames(className, 'free-lead-generator-builder', {
        'free-lead-generator-builder--desktop': isDesktop,
      })}
    >
      <div className="free-lead-generator-builder__container">
        <Box className="free-lead-generator-builder__header">
          <MarketingPromoCard
            className="free-lead-generator-builder__marketing-card"
            step={activeStepIdx + 1}
          />
          <div className="free-lead-generator-builder__header-content">
            <div className="free-lead-generator-builder__images">
              <div className="free-lead-generator-builder__badge-left">
                <img
                  src={facebookBadgeOne}
                  alt="Facebook Approval Badge"
                  width="87px"
                />
              </div>

              <Logo className="free-lead-generator-builder__logo" />

              <div className="free-lead-generator-builder__badge-right">
                <img
                  src={facebookBadgeTwo}
                  alt="Facebook Approval Badge"
                  width="110px"
                />
              </div>
            </div>

            <Stepper
              className="free-lead-generator-builder__stepper"
              markFormerComplete
              activeIdx={activeStepIdx}
              steps={steps}
            />

            {dynamicTitle && (
              <span className="free-lead-generator-builder__header-title">
                <Heading
                  size={3}
                  text={`Step ${activeStepIdx + 1}: `}
                  className="free-lead-generator-builder__attention"
                />
                <Heading size={3} text={dynamicTitle} />
              </span>
            )}

            {activeStepIdx === 0 && (
              <img
                src={HeroImage}
                className="free-lead-generator-builder__hero-img"
                alt="Facebook Hero"
              />
            )}

            {activeStepIdx === 1 && !permissionError && !connectedPage && (
              <>
                <TextLink className="free-lead-generator-builder__helper-text">
                  Great job! Now just sign in with Facebook below and choose
                  your page from the list.
                </TextLink>
                <TextLink className="free-lead-generator-builder__helper-text">
                  We&apos;ll connect your new Chatbot and you can test it out!
                </TextLink>{' '}
                <FacebookLoginButton
                  className="free-lead-generator-builder__action-btn"
                  allowManualFlow={true}
                  redirectPath={`${Routes.freeLeadGenerator.path}/start`}
                  onSuccess={onLoginSuccess}
                />
              </>
            )}

            {activeStepIdx === 1 && permissionError && (
              <>
                <Alert
                  type="error"
                  className="free-lead-generator-builder__info-message"
                >
                  Oops! Looks like we don&apos;t have the permission we need to
                  connect to your page. Let&apos;s try that again.
                </Alert>
                <PrimaryButton
                  size="lg"
                  onClick={onTryAgainBtnClick}
                  className="free-lead-generator-builder__action-btn"
                >
                  Try again
                </PrimaryButton>
              </>
            )}

            {activeStepIdx === 1 && connectedPage && (
              <>
                <Alert
                  type="success"
                  className="free-lead-generator-builder__info-message"
                  showInfoIcon
                >
                  Success! We&apos;re connected. Now just click the button below
                  to test your Free Facebook Lead Generator.
                </Alert>
                <PrimaryButton
                  size="lg"
                  onClick={onTestBtnClick}
                  className="free-lead-generator-builder__action-btn"
                >
                  Test
                </PrimaryButton>
              </>
            )}

            {isLastStep && (
              <div className="free-lead-generator-builder__email-step">
                <span className="free-lead-generator-builder__email-input-label">
                  Your email
                </span>
                <EmailInput
                  className="free-lead-generator-builder__email-input"
                  onChange={onEmailChange}
                />
              </div>
            )}
          </div>
        </Box>

        {activeStepIdx === 0 && (
          <>
            <Heading
              className="free-lead-generator-builder__title"
              size={4}
              text="Create a Dialogue for people who message your Facebook Page with the Send Message button"
            />
            {bot && (
              <StartBotsDialogueEditor
                className="free-lead-generator-builder__editor"
                chatbotId={bot?.id}
                dialogueId={bot?.page?.id}
                widgets={widgets as WidgetModel[]}
                platforms={['facebook']}
              />
            )}
          </>
        )}
        <div className="free-lead-generator-builder__footer">
          <PrimaryButton
            disabled={continueBtnDisabled}
            size="lg"
            onClick={onProceedBtnClick}
          >
            {isLastStep ? 'Finish' : 'Continue'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
